import { Grid, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';

import { ButtonDelete } from '../../../components/general/mini_components';
import { SnackbarTypeSeverity } from '../../../constants/types';
import { removeServiceFromRequestService } from '../../../services/request';
import ReasonModal from './ReasonModal';
import { canModifyRequestServices, canRemoveServiceItem } from './requestModification';
import { IRequest } from './types';
import { useRequestMutation } from './useRequestMutation';

export interface RemoveServiceActionCoreProps {
    request: IRequest;
    permissions: string[];
    loading: boolean;
    onRemove: (idRequestServiceInvestigation: number, reason: string) => void | Promise<boolean>;
}

// Presentational core: when the user may modify services, list them with a
// delete button each (disabled for the last remaining service — it must be
// cancelled, not emptied). Clicking opens a mandatory-reason confirm modal.
export const RemoveServiceActionCore: React.FC<RemoveServiceActionCoreProps> = ({ request, permissions, loading, onRemove }) => {
    const [selectedId, setSelectedId] = useState<number | null>(null);

    if (!canModifyRequestServices(request, permissions)) {
        return null;
    }
    const removable = canRemoveServiceItem(request, permissions);

    return (
        <>
            <ReasonModal
                open={selectedId !== null}
                closeModal={() => setSelectedId(null)}
                onConfirm={(reason) => onRemove(selectedId as number, reason)}
                loading={loading}
                titleId="pages.hospital.services.request.remove_service.title"
                bodyId="pages.hospital.services.request.remove_service.modal"
                reasonLabelId="pages.hospital.services.request.remove_service.reason_label"
                reasonTestId="remove-reason"
            />
            <Typography variant="h6" style={{ fontWeight: 'bold' }} gutterBottom>
                <Translate id="pages.hospital.services.request.remove_service.section_title" />
            </Typography>
            <Grid container spacing={1} alignItems="center">
                {request.requestsServiceInvestigation.map((item) => (
                    <React.Fragment key={item.id}>
                        <Grid item><Typography variant="body2">{item.serviceInvestigation?.description}</Typography></Grid>
                        <Grid item>
                            <ButtonDelete
                                data-testid={`remove-service-${item.id}`}
                                disabled={!removable || loading}
                                onClick={() => setSelectedId(item.id)}
                            />
                        </Grid>
                    </React.Fragment>
                ))}
            </Grid>
        </>
    );
};

interface RemoveServiceActionProps {
    request: IRequest;
    uuidInvestigation: string;
    permissions: string[];
    onChanged: (updated: IRequest) => void;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

// Container: wires the remove-service call to the presentational core.
const RemoveServiceAction: React.FC<RemoveServiceActionProps> = ({ request, uuidInvestigation, permissions, onChanged, setShowSnackbar }) => {
    const { loading, run } = useRequestMutation(setShowSnackbar, onChanged);

    const handleRemove = (idRequestServiceInvestigation: number, reason: string) =>
        run(() => removeServiceFromRequestService(uuidInvestigation, request.id, idRequestServiceInvestigation, reason), 'pages.hospital.services.request.remove_service.success');

    return (
        <RemoveServiceActionCore
            request={request}
            permissions={permissions}
            loading={loading}
            onRemove={handleRemove}
        />
    );
};

export default RemoveServiceAction;
